import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { EpisodeCard } from "../components/EpisodeCard";
import { GuestCard } from "../components/GuestCard";
import { ArrowRight, Quote } from "lucide-react";

export default function Home() {
    const [episodes, setEpisodes] = useState([]);
    const [guests, setGuests] = useState([]);

    useEffect(() => {
        api.get("/episodes").then((r) => setEpisodes(r.data || []));
        api.get("/guests").then((r) => setGuests(r.data || []));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount; api is a stable module singleton

    const featured = episodes[0];

    return (
        <div data-testid="home-page">
            {/* Hero */}
            <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-20 md:pb-32">
                <div className="grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7 fade-up">
                        <div className="eyebrow text-[#B8860B] mb-8 flex items-center gap-3">
                            <span className="inline-block w-10 h-px bg-[#D4AF37]" />
                            A Podcast About Small, Brave Acts
                        </div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-8">
                            The Gift of <em className="text-[#B8860B] not-italic font-serif italic">Kindness</em>,
                            <br />unwrapped weekly.
                        </h1>
                        <p className="text-lg md:text-xl text-black/65 max-w-xl mb-10 font-light leading-relaxed">
                            Conversations with the quietly generous — the volunteers, the listeners, the strangers who change strangers' lives. Sit close.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/episodes" data-testid="hero-listen-btn" className="kind-btn-primary">
                                Listen to Episodes <ArrowRight size={16} />
                            </Link>
                            <Link to="/apply" data-testid="hero-apply-btn" className="kind-btn-secondary">
                                Be a Guest
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-5 fade-up">
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1625786682948-2168238883d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                                alt="Listening"
                                className="w-full h-[480px] md:h-[600px] object-cover"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-black p-6 max-w-[240px]">
                                <Quote size={18} className="mb-2" strokeWidth={2} />
                                <p className="font-serif italic text-base leading-snug">"A small kindness, when heard, becomes a habit."</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quick Links Band */}
            <section data-testid="home-quick-links" className="border-t border-black/10 grid md:grid-cols-3">
                {[
                    { to: "/episodes", num: "01", label: "Episodes", sub: "Listen to every conversation", testid: "home-btn-episodes" },
                    { to: "/programs", num: "02", label: "Programs", sub: "Explore how we serve", testid: "home-btn-programs" },
                    { to: "/donate", num: "03", label: "Donate", sub: "Fuel the mission — tax-deductible", testid: "home-btn-donate", gold: true },
                ].map((b) => (
                    <Link
                        key={b.to}
                        to={b.to}
                        data-testid={b.testid}
                        className={`group flex items-center justify-between px-6 md:px-12 lg:px-20 py-10 md:py-12 border-b md:border-b-0 md:border-r last:border-r-0 border-black/10 transition-colors duration-300 ${
                            b.gold ? "bg-[#D4AF37] text-black hover:bg-[#0A0A0A] hover:text-[#D4AF37]" : "hover:bg-[#FAFAFA]"
                        }`}
                    >
                        <div>
                            <div className={`eyebrow text-[10px] mb-3 ${b.gold ? "text-black/60 group-hover:text-[#D4AF37]/60" : "text-[#B8860B]"}`}>{b.num}</div>
                            <div className="font-serif text-3xl md:text-4xl tracking-tight leading-none mb-2">{b.label}</div>
                            <div className={`text-sm font-light ${b.gold ? "text-black/70 group-hover:text-white/60" : "text-black/55"}`}>{b.sub}</div>
                        </div>
                        <ArrowRight size={22} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-2" />
                    </Link>
                ))}
            </section>

            {/* Marquee */}
            <section className="bg-[#0A0A0A] text-white py-6 overflow-hidden border-y border-[#D4AF37]/30">
                <div className="marquee">
                    {["a", "b"].map((dup) => (
                        <div key={`marquee-${dup}`} className="flex gap-16 shrink-0">
                            {["Listen Slow", "Give Generously", "Receive Bravely", "Speak Kindly", "Show Up", "Begin Again"].map((w) => (
                                <span key={`${dup}-${w}`} className="font-serif italic text-2xl md:text-3xl text-[#D4AF37]/90">
                                    {w} <span className="text-[#D4AF37]/40 ml-16">✦</span>
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured Episode */}
            {featured && (
                <section className="px-6 md:px-12 lg:px-20 py-20 md:py-32">
                    <div className="grid md:grid-cols-12 gap-12">
                        <div className="md:col-span-5">
                            <div className="eyebrow text-[#B8860B] mb-6">Latest Episode</div>
                            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-6 leading-tight">
                                {featured.title}
                            </h2>
                            <p className="text-black/65 font-light leading-relaxed mb-8">{featured.description}</p>
                            <Link to={`/episodes/${featured.id}`} data-testid="featured-listen-btn" className="kind-btn-gold">
                                Play Episode <ArrowRight size={16} />
                            </Link>
                        </div>
                        <div className="md:col-span-7">
                            {featured.cover_image && (
                                <img src={featured.cover_image} alt={featured.title} className="w-full h-[400px] md:h-[500px] object-cover" />
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* Recent Episodes Grid */}
            <section className="px-6 md:px-12 lg:px-20 pb-20 md:pb-32 border-t border-black/10 pt-20">
                <div className="flex justify-between items-end mb-12">
                    <div>
                        <div className="eyebrow text-[#B8860B] mb-4">The Archive</div>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Recent Conversations</h2>
                    </div>
                    <Link to="/episodes" className="eyebrow hover:text-[#B8860B] hidden md:inline-block">All Episodes →</Link>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
                    {episodes.slice(0, 3).map((ep) => <EpisodeCard key={ep.id} episode={ep} />)}
                </div>
            </section>

            {/* Guests */}
            <section className="bg-[#FAFAFA] px-6 md:px-12 lg:px-20 py-20 md:py-32 border-t border-black/10">
                <div className="grid md:grid-cols-12 gap-10 mb-16">
                    <div className="md:col-span-5">
                        <div className="eyebrow text-[#B8860B] mb-5">Collaborations</div>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight">
                            Voices we've been <em className="italic">honored</em> to host.
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 self-end">
                        <p className="text-black/65 font-light text-lg max-w-md">
                            Doctors, neighbors, artists, builders. People you'd want at your dinner table — and now, in your headphones.
                        </p>
                        <Link to="/guests" className="inline-block mt-6 eyebrow hover:text-[#B8860B]">Meet All Guests →</Link>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-10">
                    {guests.slice(0, 3).map((g) => <GuestCard key={g.id} guest={g} />)}
                </div>
            </section>

            {/* CTA: Apply */}
            <section className="bg-[#0A0A0A] text-white px-6 md:px-12 lg:px-20 py-20 md:py-32">
                <div className="grid md:grid-cols-12 items-center gap-10">
                    <div className="md:col-span-8">
                        <div className="eyebrow text-[#D4AF37] mb-6">Have a Story?</div>
                        <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.05]">
                            We are always listening for the next <em className="italic text-[#D4AF37]">quiet hero</em>.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link to="/apply" data-testid="cta-apply" className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 hover:bg-white transition-colors eyebrow">
                            Apply to be a Guest <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
